import { useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Compass, ArrowDown } from "lucide-react";

const TYPES = [
  { id: "mtg", label: "Magic: The Gathering", base: 1.6 },
  { id: "pokemon", label: "Pokémon", base: 1.3 },
  { id: "other", label: "Other TCGs", base: 0.8 },
  { id: "mixed", label: "Mixed Hoard", base: 1.0 },
];

const CONDITIONS = [
  { id: "played", label: "Well-Loved", mult: 0.5 },
  { id: "good", label: "Good", mult: 0.85 },
  { id: "nm", label: "Near Mint", mult: 1.15 },
  { id: "mint", label: "Gem Mint", mult: 1.4 },
];

const fmt = (v) => {
  const rounded = v >= 10000 ? Math.round(v / 100) * 100 : Math.round(v / 10) * 10;
  return `$${rounded.toLocaleString()}`;
};

const Estimator = ({ targetId = "offer-form" }) => {
  const [type, setType] = useState("mtg");
  const [condition, setCondition] = useState("nm");
  const [cards, setCards] = useState(3000);
  const [hits, setHits] = useState(4);

  const { low, high } = useMemo(() => {
    const t = TYPES.find((x) => x.id === type);
    const c = CONDITIONS.find((x) => x.id === condition);
    const retail = cards * t.base * c.mult * (1 + (hits / 100) * 10);
    return { low: retail * 0.55, high: retail * 0.8 };
  }, [type, condition, cards, hits]);

  const scrollToForm = () => document.getElementById(targetId)?.scrollIntoView({ behavior: "smooth" });

  return (
    <div className="card-frame grid gap-10 p-6 sm:p-10 lg:grid-cols-[1.2fr_1fr]" data-testid="estimator-widget">
      <div>
        <p className="eyebrow flex items-center gap-2">
          <Compass size={14} /> The Vault Scope
        </p>
        <h3 className="mt-3 font-serif text-xl font-bold text-cream sm:text-2xl">
          Take a playful scope reading of your hoard
        </h3>

        <div className="mt-8">
          <span className="eyebrow mb-3 block">What's in the hoard?</span>
          <div className="flex flex-wrap gap-2">
            {TYPES.map((t) => (
              <button
                key={t.id}
                type="button"
                data-testid={`estimator-type-${t.id}`}
                onClick={() => setType(t.id)}
                className={`rounded-full border px-4 py-2 font-mono text-[10px] uppercase tracking-[0.15em] transition-all duration-200 ${
                  type === t.id
                    ? "border-gold bg-gold/15 text-gold"
                    : "border-cream/15 text-cream/60 hover:border-gold/40 hover:text-cream"
                }`}
              >
                {t.label}
              </button>
            ))}
          </div>
        </div>

        <div className="mt-7">
          <div className="mb-2 flex items-center justify-between">
            <span className="eyebrow">Roughly how many cards?</span>
            <span className="font-mono text-xs text-mint" data-testid="estimator-cards-value">{cards.toLocaleString()}</span>
          </div>
          <input
            type="range"
            min="100"
            max="25000"
            step="100"
            value={cards}
            onChange={(e) => setCards(Number(e.target.value))}
            className="w-full accent-[#D8A741]"
            aria-label="Approximate number of cards"
            data-testid="estimator-cards-slider"
          />
        </div>

        <div className="mt-6">
          <div className="mb-2 flex items-center justify-between">
            <span className="eyebrow">Share of chase cards &amp; hits</span>
            <span className="font-mono text-xs text-mint" data-testid="estimator-hits-value">{hits}%</span>
          </div>
          <input
            type="range"
            min="0"
            max="15"
            step="1"
            value={hits}
            onChange={(e) => setHits(Number(e.target.value))}
            className="w-full accent-[#7AD9C2]"
            aria-label="Percentage of high-value hits"
            data-testid="estimator-hits-slider"
          />
        </div>

        <div className="mt-7">
          <span className="eyebrow mb-3 block">Overall condition</span>
          <div className="flex flex-wrap gap-2">
            {CONDITIONS.map((c) => (
              <button
                key={c.id}
                type="button"
                data-testid={`estimator-condition-${c.id}`}
                onClick={() => setCondition(c.id)}
                className={`rounded-full border px-4 py-2 font-mono text-[10px] uppercase tracking-[0.15em] transition-all duration-200 ${
                  condition === c.id
                    ? "border-mint bg-mint/15 text-mint"
                    : "border-cream/15 text-cream/60 hover:border-mint/40 hover:text-cream"
                }`}
              >
                {c.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="flex flex-col justify-center rounded-2xl border border-gold/25 bg-navy/70 p-8 text-center">
        <span className="eyebrow">Scope Reading</span>
        <p className="mt-2 text-xs text-cream/50">A hoard like this could fetch a vault offer of roughly</p>
        <AnimatePresence mode="wait">
          <motion.p
            key={`${low}-${high}`}
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -12 }}
            transition={{ duration: 0.25 }}
            className="mt-4 font-serif text-4xl font-bold tracking-tight text-gold sm:text-5xl"
            data-testid="estimator-result"
          >
            {fmt(low)} – {fmt(high)}
          </motion.p>
        </AnimatePresence>
        <p className="mx-auto mt-4 max-w-xs text-xs leading-relaxed text-cream/55">
          For fun, not an appraisal — sealed product, graded slabs, and true grails can push a real offer far
          higher. Only a human scope gets it right.
        </p>
        <button type="button" onClick={scrollToForm} className="btn-gold mx-auto mt-7" data-testid="estimator-cta">
          Get a Real Collection Offer <ArrowDown size={14} />
        </button>
      </div>
    </div>
  );
};

export default Estimator;
